import type { PaymentSyncResult } from './types';

export type PaymentReturnState = {
    phase: 'idle' | 'checking' | 'waiting' | 'succeeded' | 'canceled' | 'review' | 'timeout' | 'error' | 'loginRequired';
    message: string;
};

export class PaymentCheckError extends Error {
    constructor(message: string, public readonly retryable: boolean) { super(message); }
}

export function startPaymentStatusPolling({ check, onChange, intervalMs = 5000, timeoutMs = 120000 }: {
    check: (signal: AbortSignal) => Promise<PaymentSyncResult | null>;
    onChange: (state: PaymentReturnState) => void;
    intervalMs?: number;
    timeoutMs?: number;
}): () => void {
    const controller = new AbortController();
    let stopped = false;
    let timer: number | undefined;
    let failures = 0;
    const stop = () => {
        stopped = true;
        window.clearTimeout(timer);
        window.clearTimeout(deadline);
        controller.abort();
    };
    const finish = (phase: PaymentReturnState['phase'], message: string) => {
        if (stopped) return;
        onChange({ phase, message });
        stop();
    };
    const deadline = window.setTimeout(() => finish('timeout',
        'Подтверждение оплаты пока не получено. Проверьте статус ещё раз позже.'), timeoutMs);

    const run = async () => {
        if (stopped) return;
        try {
            const result = await check(controller.signal);
            if (stopped) return;
            failures = 0;
            if (result?.subscriptionUpdated) {
                finish('succeeded', 'Оплата подтверждена. Данные подписки обновлены.');
                return;
            }
            if (result?.payment.status === 'Canceled') {
                finish('canceled', 'Оплата отменена.');
                return;
            }
            if (result?.payment.status === 'NeedsReview') {
                finish('review', 'Платёж требует дополнительной проверки. Проверьте статус позже.');
                return;
            }
            onChange({ phase: 'waiting', message: 'Ожидаем подтверждения оплаты. Статус обновится автоматически.' });
            const nextCheck = Date.parse(result?.payment.nextCheckAt ?? '');
            const delay = Number.isFinite(nextCheck) ? Math.max(intervalMs, Math.min(10000, nextCheck - Date.now())) : intervalMs;
            timer = window.setTimeout(run, delay);
        } catch (error) {
            if (stopped) return;
            const message = error instanceof Error ? error.message : 'Не удалось проверить оплату.';
            if (error instanceof PaymentCheckError && !error.retryable) {
                finish('error', message);
                return;
            }
            failures++;
            onChange({ phase: 'waiting', message: `${message} Проверка повторится автоматически.` });
            timer = window.setTimeout(run, Math.min(15000, intervalMs * 2 ** Math.min(failures, 2)));
        }
    };
    onChange({ phase: 'checking', message: 'Проверяем оплату…' });
    // Deferring the first request also avoids duplicate requests in StrictMode.
    timer = window.setTimeout(run, 0);
    return stop;
}
